import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faFlagCheckered, faLink, faUser, faEdit, faLayerGroup, faExclamationTriangle, faStickyNote, faPlus, faSpinner, faColumns, faChartLine, faChevronRight, faTrash } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmModal from './components/ConfirmModal';
import VagueBadge from './components/VagueBadge';
import PaymentRelanceCard from './components/PaymentRelanceCard';
import { useModalDismiss } from '../../hooks/useModalDismiss';

const STEP_LABELS = { setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing' };
const STEP_COLORS = { setup: '#f59e0b', terrain: '#10b981', reporting: '#0ea5e9', closing: '#8b5cf6' };
const PAYMENT_LABELS = { per_complete: 'Par complété', per_day: 'Par jour' };

const SlotDetail = () => {
  const { id: projectId, slotId } = useParams();
  const navigate = useNavigate();
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectAssignees, setProjectAssignees] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [checklists, setChecklists] = useState([]);
  const [newCheckLabel, setNewCheckLabel] = useState('');
  const [addingCheck, setAddingCheck] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: '', reasons: [] });

  const errorDismiss = useModalDismiss(
    () => setErrorModal({ open: false, message: '', reasons: [] }), { enabled: errorModal.open });
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectAttestationEmail, setProjectAttestationEmail] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [slotRes, projectRes] = await Promise.all([
        api.get(`/projects/${projectId}/slots/${slotId}`),
        api.get(`/projects/${projectId}`),
      ]);
      const s = slotRes.data.data;
      setSlot(s);
      setChecklists(s.checklists || []);
      setProjectAssignees(projectRes.data.data?.assignees || []);
      setProjectName(projectRes.data.data?.name || '');
      setProjectAttestationEmail(projectRes.data.data?.attestation_email || '');
    } catch {
      navigate(`/projects/${projectId}?tab=surveys`);
    } finally {
      setLoading(false);
    }
  }, [projectId, slotId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleChecklist = async (itemId) => {
    setChecklists(prev => prev.map(c => c.id === itemId ? { ...c, is_completed: !c.is_completed } : c));
    try {
      await api.post(`/projects/${projectId}/checklists/${itemId}/toggle`);
      const res = await api.get(`/projects/${projectId}/slots/${slotId}`);
      setChecklists(res.data.data.checklists || []);
    } catch {
      setChecklists(prev => prev.map(c => c.id === itemId ? { ...c, is_completed: !c.is_completed } : c));
    }
  };

  const handleAddChecklist = async () => {
    if (!newCheckLabel.trim() || addingCheck || !slot) return;
    setAddingCheck(true);
    try {
      const res = await api.post(`/projects/${projectId}/checklists`, {
        step: slot.step || 'setup',
        label: newCheckLabel.trim(),
        survey_slot_id: parseInt(slotId),
      });
      setChecklists(prev => [...prev, res.data.data]);
      setNewCheckLabel('');
    } catch { }
    finally { setAddingCheck(false); }
  };

  const handleToggleFinished = async () => {
    setActionLoading(true);
    try {
      await api.post(`/projects/${projectId}/slots/${slotId}/toggle-finished`);
      fetchData();
    } catch (err) {
      const data = err.response?.data;
      if (data?.reasons) {
        setErrorModal({ open: true, message: data.message, reasons: data.reasons });
      } else {
        setErrorModal({ open: true, message: data?.message || 'Erreur', reasons: [] });
      }
    } finally { setActionLoading(false); }
  };

  const startEditNotes = () => {
    setNotesValue(slot.notes || '');
    setEditingNotes(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/projects/${projectId}/slots/${slotId}`);
      navigate(`/projects/${projectId}?tab=surveys`);
    } catch (err) {
      const data = err.response?.data;
      setErrorModal({ open: true, message: data?.message || 'Erreur lors de la suppression', reasons: [] });
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/projects/${projectId}/slots/${slotId}/notes`, { notes: notesValue });
      setSlot(prev => ({ ...prev, notes: notesValue }));
      setEditingNotes(false);
    } catch { }
    finally { setSavingNotes(false); }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" />
      </div>
    );
  }

  if (!slot) return null;

  const links = slot.linked_surveys || [];
  const isLinked = links.length > 0 || !!slot.survey_id;
  // The detail grid describes one survey; with several we show a compact list instead.
  // Shared date formatter. The API serialises date casts as UTC ISO, so midnight local
  // arrives as 23:00 the previous day; new Date(...) + toLocaleDateString reads it back in
  // the browser's zone and restores the intended day. Never format these by string-slicing
  // the ISO value, which shows the day before.
  const fmtDay = v => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');
  const stepColor = STEP_COLORS[slot.step] || '#6b7280';
  const primarySurvey = links[0]?.survey || slot.survey;
  // A slot can cover CATI and F2F surveys at once, so the header shows every type present.
  const linkTypes = [...new Set(
    (links.length > 0 ? links.map(l => l.survey) : [slot.survey])
      .filter(Boolean)
      .map(sv => (sv.poste_qcode === 'ref' ? 'F2F' : 'CATI'))
  )];
  const canEdit = !!slot.can_edit;

  const slotNames = (slot.assignees || []).map(a => a.name);
  const projectOnlyNames = projectAssignees.filter(a => !slotNames.includes(a.name)).map(a => a.name);
  const allAssigneeNames = [...slotNames, ...projectOnlyNames];

  return (
    <div style={{ padding: '20px 28px', maxWidth: 750, margin: '0 auto' }}>
      {/* Breadcrumb navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, flexWrap: 'wrap', fontSize: '0.78rem' }}>
        <span onClick={() => navigate('/projects')} style={crumbLink}>
          <FontAwesomeIcon icon={faColumns} size="xs" style={{ marginRight: 5, opacity: 0.6 }} />Kanban
        </span>
        <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.6rem', color: '#d1d5db' }} />
        <span onClick={() => navigate(`/projects/${projectId}?tab=surveys`)} style={crumbLink}>
          {projectName || 'Projet'}
        </span>
        <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.6rem', color: '#d1d5db' }} />
        <span style={{ color: '#111827', fontWeight: 600 }}>{slot.name}</span>

        {isLinked && primarySurvey && user?.role !== 'resp_admin' && (
          <span onClick={() => navigate(`/cati/surveys/${primarySurvey.id}/stats`)}
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, color: '#dc2626', fontWeight: 600, cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faChartLine} size="xs" /> Statistiques
          </span>
        )}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
            {slot.name}
          </h5>
          {slot.wave && (
            <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FontAwesomeIcon icon={faLayerGroup} size="xs" /> {slot.wave.name}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Terminé toggle */}
          {isLinked && canEdit && (
            <button onClick={handleToggleFinished} disabled={actionLoading}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                border: slot.is_finished ? '1px solid #10b981' : '1px solid #d1d5db',
                background: slot.is_finished ? '#f0fdf4' : '#fff',
                color: slot.is_finished ? '#166534' : '#6b7280',
                opacity: actionLoading ? 0.5 : 1,
              }}>
              <FontAwesomeIcon icon={faFlagCheckered} size="sm" className="me-1" />
              {slot.is_finished ? 'Terminée' : 'Marquer terminée'}
            </button>
          )}
          {/* Read-only terminé badge for non-editors */}
          {isLinked && !canEdit && slot.is_finished && (
            <span style={{
              padding: '7px 14px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600,
              background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
            }}>
              <FontAwesomeIcon icon={faFlagCheckered} size="xs" className="me-1" />Terminée
            </span>
          )}
          {/* Modifier button */}
          {canEdit && (
            <button onClick={() => navigate(`/projects/${projectId}/slots/${slotId}/edit`)}
              style={{
                padding: '7px 14px', borderRadius: 8, border: '1px solid #d1d5db',
                background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
              }}>
              <FontAwesomeIcon icon={faEdit} size="sm" className="me-1" />Modifier
            </button>
          )}
          {/* Supprimer button — setup-step only */}
          {canEdit && slot.step === 'setup' && !slot.is_finished && (
            <button onClick={() => setDeleteConfirm(true)}
              style={{
                padding: '7px 14px', borderRadius: 8, border: '1px solid #fecaca',
                background: '#fff', color: '#dc2626', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
              }}>
              <FontAwesomeIcon icon={faTrash} size="sm" className="me-1" />Supprimer
            </button>
          )}
        </div>
      </div>

      {/* Status bar */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 20px', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: stepColor }} />
          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: stepColor }}>{STEP_LABELS[slot.step] || slot.step}</span>
        </div>
        {isLinked ? (
          <span style={{ display: 'flex', gap: 4 }}>
            {linkTypes.map(t => (
              <span key={t} style={{
                fontSize: '0.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                background: t === 'CATI' ? '#f3e8ff' : '#dbeafe', color: t === 'CATI' ? '#6b21a8' : '#1e40af',
              }}>
                {t}
              </span>
            ))}
          </span>
        ) : (
          <span style={{ fontSize: '0.72rem', color: '#a16207', display: 'flex', alignItems: 'center', gap: 4 }}>
            <FontAwesomeIcon icon={faClock} size="xs" /> Non liée
          </span>
        )}

        {/* Assignees */}
        {allAssigneeNames.length > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', color: '#6b7280' }}>
            <FontAwesomeIcon icon={faUser} size="xs" style={{ color: '#d1d5db' }} />
            <span>{allAssigneeNames.join(', ')}</span>
          </div>
        )}
      </div>

      {/* Slot info */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px', marginBottom: 16 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16 }}>
          Informations de l'enquête
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <DetailField label="Nom" value={slot.name} />
          <DetailField label="Groupe" value={slot.wave?.name || 'Sans groupe'} />
          <DetailField label="Assigné(s)" value={allAssigneeNames.length > 0 ? allAssigneeNames.join(', ') : 'Aucun'} />
          {(() => {
            const eff = slot.attestation_email || projectAttestationEmail;
            const inherited = !slot.attestation_email && projectAttestationEmail;
            return (
              <DetailField
                label="Email client (attest. réf)"
                value={eff ? `${eff}${inherited ? ' (hérité du projet)' : ''}` : '—'}
              />
            );
          })()}
        </div>
      </div>

      {/* Dates closing */}
      {(slot.step === 'closing' || slot.livrables_deadline || slot.reception_date || slot.facturation_date || slot.attente_paiement_date || slot.payment_date) && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px', marginBottom: 16 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16 }}>
            Dates closing
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <DetailField label="Échéance livrables" value={fmtD(slot.livrables_deadline)} />
            <DetailField label="Réception" value={fmtD(slot.reception_date)} />
            <DetailField label="Facturation" value={fmtD(slot.facturation_date)} />
            <DetailField label="En attente de paiement" value={fmtD(slot.attente_paiement_date)} />
            <DetailField label="Date de paiement (encaissé)" value={fmtD(slot.payment_date)} />
          </div>
        </div>
      )}

      {/* Délai de relance paiement */}
      {slot.step === 'closing'
        && ['facturation', 'attente_paiement'].includes(slot.closing_step)
        && slot.payment_relance && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px', marginBottom: 16 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16 }}>
            Relance paiement
          </div>
          <PaymentRelanceCard
            projectId={projectId}
            slotId={slot.id}
            state={slot.payment_relance}
            canEdit={!!slot.can_set_payment_relance}
            onSaved={() => fetchData()}
          />
        </div>
      )}

      {/* Linked survey */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16 }}>
          {links.length > 1 ? `Enquêtes liées (${links.length})` : 'Enquête liée'}
        </div>

        {isLinked ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Full detail block per linked survey — same layout whether the slot covers
                one survey or several. */}
            {links.map((l, idx) => {
              const sv = l.survey || {};
              const cati = sv.poste_qcode !== 'ref';
              const wv = l.survey_wave;
              const waveName = wv ? (wv.label || `Vague ${(wv.order_index ?? 0) + 1}`) : null;
              return (
                <div key={l.id} style={{
                  display: 'flex', flexDirection: 'column', gap: 12,
                  paddingTop: idx === 0 ? 0 : 20,
                  borderTop: idx === 0 ? 'none' : '1px solid #f3f4f6',
                }}>
                  {/* Survey identity */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
                    <FontAwesomeIcon icon={faLink} style={{ color: '#9ca3af' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {sv.title}
                        <VagueBadge wave={wv} style={{ fontSize: '0.6rem', padding: '2px 7px' }} />
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>LimeSurvey ID: {sv.limesurvey_id}</div>
                    </div>
                    <span style={{
                      fontSize: '0.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                      background: cati ? '#f3e8ff' : '#dbeafe', color: cati ? '#6b21a8' : '#1e40af',
                    }}>
                      {cati ? 'CATI' : 'F2F'}
                    </span>
                  </div>

                  {/* Survey details grid — adapts per type */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <DetailField label="Date début" value={fmtDay(sv.date_debut)} />
                    <DetailField label="Date fin" value={fmtDay(sv.date_fin)} />

                    {/* Vague-scoped link: its window follows the survey's, and its
                        objective sits beside the global one — each pair on its own row. */}
                    {wv && <DetailField label={`Début ${waveName}`} value={fmtDay(wv.date_debut)} />}
                    {wv && <DetailField label={`Fin ${waveName}`} value={fmtDay(wv.date_fin)} />}

                    <DetailField label="Target global" value={sv.general_target || '—'} />
                    {wv && <DetailField label={`Target ${waveName}`} value={wv.target ?? '—'} />}

                    <DetailField label="Contrôle (%)" value={sv.control_percentage ? `${sv.control_percentage}%` : '—'} />

                    {/* F2F fields */}
                    {!cati && (
                      <>
                        <DetailField label="Prix / complété" value={sv.price_per_complete ? `${Number(sv.price_per_complete).toLocaleString('fr-FR')} MAD` : '—'} />
                        <DetailField label="Frais de déplacement" value={sv.frais_deplacement ? `${Number(sv.frais_deplacement).toLocaleString('fr-FR')} MAD` : '—'} />
                        <DetailField label="Marge imprévus" value={sv.marge_imprevus ? `${Number(sv.marge_imprevus).toLocaleString('fr-FR')} MAD` : '—'} />
                      </>
                    )}

                    {/* CATI per_complete fields */}
                    {cati && sv.payment_type === 'per_complete' && (
                      <DetailField label="Prix / complété" value={sv.price_per_complete ? `${Number(sv.price_per_complete).toLocaleString('fr-FR')} MAD` : '—'} />
                    )}

                    {/* CATI per_day fields */}
                    {cati && sv.payment_type === 'per_day' && (
                      <>
                        <DetailField label="Prix / jour" value={sv.price_per_day ? `${Number(sv.price_per_day).toLocaleString('fr-FR')} MAD` : '—'} />
                        <DetailField label="Jours prévus" value={sv.target_days || '—'} />
                        <DetailField label="Enquêteurs prévus" value={sv.target_interviewers || '—'} />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '0.82rem', color: '#d1d5db' }}>Aucune enquête liée</div>
          </div>
        )}
      </div>

      {/* Notes */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px', marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FontAwesomeIcon icon={faStickyNote} size="xs" /> Notes
          </div>
          {canEdit && !editingNotes && (
            <button onClick={startEditNotes}
              style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.72rem', fontWeight: 500, cursor: 'pointer' }}>
              <FontAwesomeIcon icon={slot.notes ? faEdit : faPlus} size="xs" className="me-1" />
              {slot.notes ? 'Modifier' : 'Ajouter'}
            </button>
          )}
        </div>

        {editingNotes ? (
          <div>
            <textarea
              value={notesValue}
              onChange={e => setNotesValue(e.target.value)}
              rows={4}
              placeholder="Ajouter une note..."
              autoFocus
              style={{ width: '100%', fontSize: '0.82rem', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button onClick={() => setEditingNotes(false)} disabled={savingNotes}
                style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={handleSaveNotes} disabled={savingNotes}
                style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', opacity: savingNotes ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                {savingNotes && <FontAwesomeIcon icon={faSpinner} spin size="xs" />}
                Enregistrer
              </button>
            </div>
          </div>
        ) : (
          slot.notes ? (
            <div style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{slot.notes}</div>
          ) : (
            <div style={{ fontSize: '0.78rem', color: '#d1d5db' }}>Aucune note</div>
          )
        )}
      </div>

      {/* Checklists */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px', marginTop: 16 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
          Checklist{checklists.length > 0 ? ` · ${checklists.filter(c => c.is_completed).length}/${checklists.length}` : ''}
        </div>
        {(() => {
          const byStep = {};
          checklists.forEach(item => { const s = item.step || 'autre'; if (!byStep[s]) byStep[s] = []; byStep[s].push(item); });
          const order = ['ao', 'setup', 'terrain', 'reporting', 'closing', 'autre'];
          return Object.keys(byStep).sort((a, b) => order.indexOf(a) - order.indexOf(b)).map(step => {
            const items = byStep[step];
            const accent = STEP_COLORS[step] || '#9e9e9e';
            const stepDone = items.filter(i => i.is_completed).length;
            return (
              <div key={step} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, paddingBottom: 4, borderBottom: `1.5px solid ${accent}20` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{STEP_LABELS[step] || step}</span>
                  </div>
                  <span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#bdbdbd' }}>{stepDone}/{items.length}</span>
                </div>
                {items.map(item => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f9fafb' }}>
                    <div
                      onClick={() => handleToggleChecklist(item.id)}
                      style={{
                        width: 16, height: 16, borderRadius: 3, flexShrink: 0, cursor: 'pointer',
                        border: item.is_completed ? 'none' : '2px solid #d1d5db',
                        background: item.is_completed ? '#dc2626' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                      }}>
                      {item.is_completed && <svg width="8" height="6" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: '0.78rem', color: item.is_completed ? '#9ca3af' : '#111827',
                        textDecoration: item.is_completed ? 'line-through' : 'none',
                        fontWeight: item.is_completed ? 400 : 500,
                      }}>
                        {item.label}
                      </span>
                      {item.is_completed && item.completed_by && (
                        <div style={{ fontSize: '0.62rem', color: '#d1d5db', marginTop: 1 }}>
                          {item.completed_by.name} {item.completed_at ? `· ${new Date(item.completed_at).toLocaleDateString('fr-FR')}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          });
        })()}
        {checklists.length === 0 && (
          <div style={{ fontSize: '0.78rem', color: '#d1d5db', padding: '8px 0' }}>Aucun élément</div>
        )}
        {/* Add input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
          <input
            type="text"
            value={newCheckLabel}
            onChange={e => setNewCheckLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddChecklist(); }}
            placeholder="Ajouter un élément..."
            style={{ flex: 1, fontSize: '0.75rem', padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none' }}
          />
          <button
            onClick={handleAddChecklist}
            disabled={!newCheckLabel.trim() || addingCheck}
            style={{
              padding: '4px 10px', borderRadius: 6, border: 'none',
              background: newCheckLabel.trim() ? '#dc2626' : '#e5e7eb',
              color: newCheckLabel.trim() ? '#fff' : '#9ca3af',
              cursor: 'pointer', fontSize: '0.72rem',
            }}>
            +
          </button>
        </div>
      </div>

      {/* Error modal with reasons */}
      {errorModal.open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div {...errorDismiss} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
          <div style={{ position: 'relative', background: '#fff', borderRadius: 12, padding: '28px 32px', width: 420, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#dc2626', fontSize: '0.9rem' }} />
              </div>
              <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>{errorModal.message}</h5>
            </div>
            {errorModal.reasons.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                {errorModal.reasons.map((r, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', color: '#991b1b', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} size="xs" />{r}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setErrorModal({ open: false, message: '', reasons: [] })}
                style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm}
        onClose={() => !deleting && setDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer cette enquête ?"
        message={`« ${slot.name} » sera supprimée définitivement avec sa checklist. Cette action est irréversible.`}
        variant="danger"
        confirmLabel={deleting ? 'Suppression...' : 'Supprimer'}
      />
    </div>
  );
};

const fmtD = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const crumbLink = {
  color: '#6b7280', cursor: 'pointer', fontWeight: 500,
  display: 'flex', alignItems: 'center',
};

const DetailField = ({ label, value }) => (
  <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: 6 }}>
    <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: '0.82rem', fontWeight: 500, color: '#111827' }}>{value}</div>
  </div>
);

export default SlotDetail;
